import { forwardRef, useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { hasAtLeast, isEmpty } from 'remeda'
import { useEventListener, useMediaQuery } from 'usehooks-ts'
import { ComplementaryProductsContainer } from '@components/ComplementaryProductsContainer/ComplementaryProductsContainer'
import { ProductsSlider } from '@components/ProductsSlider/ProductsSlider'
import { dataTestIds } from '@velasca/constants'
import { ResponsiveImage } from '@velasca/ui/responsive-image'
import { Video } from '@velasca/ui/video'
import { buildIntrinsicWidthSources } from '@velasca/utils/build-intrinsic-width-sources'
import { buildWidthSources } from '@velasca/utils/build-width-sources'
import { setQueryParams } from '@velasca/utils/set-query-params'
import { trackEvent } from '@velasca/utils/track-event'
import * as S from './ComplementaryProducts.styles'
import type { ComplementaryProductsProps } from './ComplementaryProducts.types'

const quickAddEvent = (handle: string) => {
  trackEvent({
    googleAnalytics: {
      eventName: 'complementary_quick_add',
      data: { handle },
    },
  })
}

const addToCartEvent = (handle: string) => {
  trackEvent({
    googleAnalytics: {
      eventName: 'complementary_quick_add_to_cart',
      data: { handle },
    },
  })
}

export const ComplementaryProducts = forwardRef<
  HTMLDivElement,
  ComplementaryProductsProps
>(
  (
    {
      className,
      sliderStyles,
      products,
      loading = false,
      parentLoading = false,
      arrowVisible = false,
      breakpoints,
      editorialImage,
      editorialVideo,
      onQuickAdd,
      onAddToCart,
      layout = 'standard',
      loadingSlides,
      title,
      subTitle,
      shouldOpenCartDrawerOnAddToCart,
    },
    ref,
  ) => {
    const editorialImageRef = useRef<HTMLImageElement>(null)
    const productListRef = useRef<HTMLDivElement>(null)

    const isDesktopViewport = useMediaQuery('(min-width: 992px)')
    const { t } = useTranslation('product')

    const isRedesignMode = useMemo(
      () => isDesktopViewport && (editorialImage || editorialVideo),
      [isDesktopViewport, editorialImage, editorialVideo],
    )

    /**
     * This is necessary in "Buy the Look" sections at the bottom of some lookbook pages.
     */
    const matchHeights = useCallback(() => {
      if (!editorialImageRef.current || !productListRef.current) {
        return
      }

      const editorialImageRect =
        editorialImageRef.current.getBoundingClientRect()

      productListRef.current.style.height = `${editorialImageRect.height}px`
    }, [])

    useEventListener('resize', matchHeights)

    useEffect(() => {
      matchHeights()
    }, [matchHeights])

    const handleQuickAdd = useCallback(
      (handle: string | undefined) => {
        if (handle) {
          onQuickAdd ? onQuickAdd(handle) : quickAddEvent(handle)
        }
      },
      [onQuickAdd],
    )

    const handleAddToCart = useCallback(
      (handle: string | undefined) => {
        if (handle) {
          onAddToCart ? onAddToCart(handle) : addToCartEvent(handle)
        }
      },
      [onAddToCart],
    )

    const slider = useMemo(() => {
      return (
        <ProductsSlider
          showProductType
          shouldOpenCartDrawerOnAddToCart={shouldOpenCartDrawerOnAddToCart}
          className={`product-slider ${className}`}
          sliderStyles={sliderStyles}
          loadingSlides={loadingSlides}
          products={products}
          loading={loading || parentLoading}
          arrowVisible={arrowVisible}
          breakpoints={breakpoints}
          dataTestId={dataTestIds.COMPLEMENTARY_PRODUCTS_SLIDER}
          title={title ?? t('crossProductsTitle')}
          subTitle={subTitle}
          onQuickAdd={handleQuickAdd}
          onAddToCart={handleAddToCart}
        />
      )
    }, [
      shouldOpenCartDrawerOnAddToCart,
      className,
      sliderStyles,
      loadingSlides,
      products,
      loading,
      parentLoading,
      arrowVisible,
      breakpoints,
      title,
      t,
      subTitle,
      handleQuickAdd,
      handleAddToCart,
    ])

    const productsWithoutSingleSizesUnavailable = products?.filter(
      (product) => {
        const isProductMultiSize = hasAtLeast(product.sizes, 2)
        const isProductSingleSizeUnavailable =
          product.sizes.length === 1 &&
          product.sizes.every((size) => size.quantityAvailable === 0)

        return isProductMultiSize || !isProductSingleSizeUnavailable
      },
    )

    const list = useMemo(() => {
      return (
        <ComplementaryProductsContainer
          showProductType
          loading={loading || parentLoading}
          dataTestId={dataTestIds.COMPLEMENTARY_PRODUCTS_SLIDER}
          ref={productListRef}
          products={productsWithoutSingleSizesUnavailable}
          layout={layout}
          productListRef={productListRef}
          onQuickAdd={handleQuickAdd}
          onAddToCart={handleAddToCart}
        />
      )
    }, [
      loading,
      parentLoading,
      productsWithoutSingleSizesUnavailable,
      layout,
      handleQuickAdd,
      handleAddToCart,
    ])

    if ((!products || isEmpty(products)) && !loading) {
      return null
    }

    const desktopImageWidth = Math.floor(1290 * 0.5)

    // Desktop image configuration
    const desktopImageConfig = editorialImage
      ? buildWidthSources(
          editorialImage,
          [{ maxWidth: 1200, imageWidth: desktopImageWidth }],
          desktopImageWidth,
        )
      : null

    return (
      <S.Container ref={ref} layout={layout}>
        {isRedesignMode && (editorialImage || editorialVideo) ? (
          <>
            <S.Title>{title ?? t('crossProductsTitle')}</S.Title>
            <S.Content layout={layout}>
              <S.ImageContainer>
                {editorialImage && !editorialVideo && (
                  <ResponsiveImage
                    lazyload
                    lqip
                    ref={editorialImageRef}
                    sizes={desktopImageConfig?.sizes}
                    sources={buildIntrinsicWidthSources({
                      src: editorialImage,
                    })}
                    imgSrc={setQueryParams(editorialImage, [
                      { name: 'width', value: '30' },
                    ])}
                    onLoad={matchHeights}
                  />
                )}
                {editorialVideo && !editorialImage && (
                  <Video
                    poster={editorialVideo.poster}
                    sources={editorialVideo.sources}
                  />
                )}
              </S.ImageContainer>
              {list}
            </S.Content>
          </>
        ) : (
          slider
        )}
      </S.Container>
    )
  },
)

ComplementaryProducts.displayName = 'ComplementaryProducts'
